import { CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BasePage from 'components/page/BasePage';
import { SeasonListItem } from 'type/seasonType';

import { seasonService } from '../../service/seasonService';
import { canApprove, rowStatusColor } from '../handlers';
import { bulkEpisodeService } from '../services';
import {
  BulkEpisodeDryRunResult,
  BulkEpisodeJobDetail,
  BulkEpisodeJobListItem,
  BulkEpisodeRowResult,
} from '../types';

// 필수 필드는 헤더에 * 로 표시 (파서가 * 를 무시하므로 그대로 업로드 가능)
const TEMPLATE_HEADER =
  'episode_no*,video_path*,on_air_date,publish_date,slug,episode_order,episode_type';
const TEMPLATE_EXAMPLE =
  '1,ys_test/example_ep1.mp4,2024-01-01,2026-07-01 09:00:00,,1,';

const CSV_HINT =
  '필수(*): episode_no, video_path. ' +
  'video_path 는 asset-source(backedup/) 하위 상대경로 (예: ys_test/ep001.mp4). ' +
  '제목은 시즌명+화수로 자동 생성됨. ' +
  '선택: on_air_date, publish_date(odk 공개 시작일, YYYY-MM-DD 또는 ' +
  'YYYY-MM-DD HH:MM:SS · 미 서부 기준), ' +
  'slug(빈 값이면 기존 규칙 자동 생성), episode_order, ' +
  'episode_type(허용값: tv-series · shorts · clip · trailer · movie · additional · ' +
  'live_streaming — 미지정 시 tv-series).';

const OVERWRITE_HINT =
  '기존 에피소드 덮어쓰기: slug 컬럼에 그 에피소드의 slug 를 입력하면 새로 만들지 않고 ' +
  '영상·on_air_date·publish_date 를 덮어씁니다 (빈 날짜는 기존 값 유지). ' +
  '미리보기에서 해당 행은 replace 로 표시되고 Target 에서 대상 에피소드를 확인할 수 있습니다.';

const downloadTemplate = () => {
  const csv = `﻿${TEMPLATE_HEADER}\n${TEMPLATE_EXAMPLE}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bulk-episode-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const JOB_STATUS_COLOR: Record<string, string> = {
  staged: 'default',
  previewed: 'default',
  approved: 'gold',
  committing: 'blue',
  committed: 'green',
  failed: 'red',
};

const IN_PROGRESS = new Set(['approved', 'committing']);
// 승인 전(미리보기/스테이징) 잡은 목록에서 숨김 — 업로드마다 dry-run 잡이 생기므로
const HIDDEN_STATUSES = new Set(['staged', 'previewed']);

const BulkEpisodeIngest: React.FC = () => {
  const [seasons, setSeasons] = useState<SeasonListItem[]>([]);
  const [seasonId, setSeasonId] = useState<number | undefined>(undefined);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [dryRun, setDryRun] = useState<BulkEpisodeDryRunResult | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [jobs, setJobs] = useState<BulkEpisodeJobListItem[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [detailJob, setDetailJob] = useState<BulkEpisodeJobDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  const loadSeasons = async (search?: string) => {
    setLoadingSeasons(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      params.set('page_size', '50');
      const res = await seasonService.fetchSeasonList(params);
      setSeasons(res.results);
    } catch (err) {
      message.error((err as Error).message ?? '시즌 목록 로드 실패');
    } finally {
      setLoadingSeasons(false);
    }
  };

  const loadJobs = async () => {
    setJobsLoading(true);
    try {
      const res = await bulkEpisodeService.getJobs();
      setJobs(res.jobs);
    } catch {
      // 폴링 실패는 조용히 무시
    } finally {
      setJobsLoading(false);
    }
  };

  const openJobDetail = async (jobId: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailJob(null);
    try {
      const detail = await bulkEpisodeService.getJob(jobId);
      setDetailJob(detail);
    } catch (err) {
      message.error((err as Error).message ?? '상세 로드 실패');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadSeasons();
    loadJobs();
    // 진행중 잡 추적을 위해 주기적으로 목록 새로고침 (DB 기반이라 새로고침/이탈해도 유지)
    const timer = setInterval(loadJobs, 8000);
    // 시즌 상세에서 ?season_id 로 진입한 경우 해당 시즌을 미리 선택
    const sid = searchParams.get('season_id');
    if (sid) {
      const id = Number(sid);
      setSeasonId(id);
      seasonService
        .fetchById(id)
        .then((s) => {
          if (s && s.id) {
            setSeasons((prev) =>
              prev.some((x) => x.id === s.id)
                ? prev
                : [
                    {
                      id: s.id,
                      title: s.title,
                      slug: s.slug,
                      status: '',
                    } as SeasonListItem,
                    ...prev,
                  ]
            );
          }
        })
        .catch(() => undefined);
    }
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickFile = () => {
    if (!seasonId) {
      message.warning('먼저 시즌(프로그램)을 선택하세요.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !seasonId) {
      return;
    }
    e.target.value = '';
    setLoading(true);
    setPreviewOpen(true);
    setDryRun(null);
    try {
      const result = await bulkEpisodeService.createDryRun(file, seasonId);
      setDryRun(result);
    } catch (err) {
      message.error((err as Error).message ?? '업로드 실패');
      setPreviewOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!dryRun) {
      return;
    }
    setApproving(true);
    try {
      // 비동기 승인(202): 백그라운드(AWS Batch)에서 처리. 모달을 닫고 아래 목록에서 추적.
      await bulkEpisodeService.approve(dryRun.job_id);
      message.success('승인됨 — 아래 "최근 작업" 목록에서 진행 상황을 확인하세요.');
      setPreviewOpen(false);
      setDryRun(null);
      loadJobs();
    } catch (err) {
      message.error((err as Error).message ?? '승인 실패');
    } finally {
      setApproving(false);
    }
  };

  const columns = [
    { title: 'Ep', dataIndex: 'episode_no', key: 'episode_no', width: 60 },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Video', dataIndex: 'video_path', key: 'video_path' },
    {
      title: 'On-Air',
      dataIndex: 'on_air_date',
      key: 'on_air_date',
      width: 110,
      render: (v: string) => v || '-',
    },
    {
      title: 'Publish (PT)',
      dataIndex: 'publish_date',
      key: 'publish_date',
      width: 160,
      render: (v: string) => v || '-',
    },
    {
      title: 'Type',
      dataIndex: 'episode_type',
      key: 'episode_type',
      width: 90,
      render: (v: string) => v || '-',
    },
    {
      title: 'Status',
      dataIndex: 'mapping_status',
      key: 'mapping_status',
      width: 110,
      render: (status: string) => <Tag color={rowStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Target',
      dataIndex: 'target_content_id',
      key: 'target_content_id',
      width: 90,
      // replace 행의 덮어쓰기 대상 에피소드 — 승인 전에 확인 가능
      render: (id: number | null) => (id ? <Link to={`/episode/edit/${id}`}>{id}</Link> : '-'),
    },
    {
      title: 'Warnings',
      dataIndex: 'errors',
      key: 'errors',
      render: (errors: string[]) => (errors && errors.length ? errors.join(', ') : '-'),
    },
  ];

  const jobColumns = [
    { title: 'Job', dataIndex: 'job_id', key: 'job_id', width: 70 },
    {
      title: 'Season',
      dataIndex: 'season_title',
      key: 'season_title',
      render: (t: string | null, r: BulkEpisodeJobListItem) =>
        r.season_id ? (
          <Link to={`/season/edit2/${r.season_id}`}>{t ?? r.season_id}</Link>
        ) : (
          '-'
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s: string) => <Tag color={JOB_STATUS_COLOR[s] ?? 'default'}>{s}</Tag>,
    },
    {
      title: 'Created',
      key: 'created_cnt',
      width: 80,
      render: (_: unknown, r: BulkEpisodeJobListItem) => r.stats?.created ?? '-',
    },
    {
      title: 'Replaced',
      key: 'replaced_cnt',
      width: 80,
      render: (_: unknown, r: BulkEpisodeJobListItem) => r.stats?.replaced ?? '-',
    },
    {
      title: 'Failed',
      key: 'failed_cnt',
      width: 80,
      render: (_: unknown, r: BulkEpisodeJobListItem) => r.stats?.failed ?? '-',
    },
    {
      title: 'Time',
      dataIndex: 'created',
      key: 'created',
      render: (c: string | null) => (c ? new Date(c).toLocaleString() : '-'),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, r: BulkEpisodeJobListItem) => (
        <Button size="small" onClick={() => openJobDetail(r.job_id)}>
          상세
        </Button>
      ),
    },
  ];

  const matched = dryRun?.stats.matched ?? 0;
  const replaceCnt = dryRun?.stats.replace ?? 0;
  // 승인된 잡만 노출 (미리보기 단계 잡 숨김)
  const visibleJobs = jobs.filter((j) => !HIDDEN_STATUSES.has(j.status));
  const hasInProgress = visibleJobs.some((j) => IN_PROGRESS.has(j.status));

  return (
    <BasePage>
      <Card title="Bulk Episode Ingest (M4)">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Select
              showSearch
              placeholder="시즌(프로그램) 선택"
              style={{ width: 380 }}
              loading={loadingSeasons}
              filterOption={false}
              onSearch={loadSeasons}
              onChange={(value: number) => setSeasonId(value)}
              value={seasonId}
              options={seasons.map((s) => ({
                value: s.id,
                label: `${s.title} (${s.slug})`,
              }))}
            />
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={handlePickFile}
              disabled={!seasonId}
            >
              CSV 업로드
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
          </Space>
          <Alert
            type="info"
            showIcon
            message={CSV_HINT}
            action={
              <Button size="small" onClick={downloadTemplate}>
                템플릿 다운받기
              </Button>
            }
          />
          <Alert type="warning" showIcon message={OVERWRITE_HINT} />

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              최근 작업 {hasInProgress ? '(진행 중…)' : ''}
            </Typography.Title>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={jobsLoading}
              onClick={loadJobs}
            >
              새로고침
            </Button>
          </Space>
          <Table<BulkEpisodeJobListItem>
            rowKey="job_id"
            size="small"
            pagination={false}
            dataSource={visibleJobs}
            columns={jobColumns}
            scroll={{ y: 320 }}
          />
        </Space>

        <Modal
          title="미리보기 (dry-run) — 승인 시 백그라운드 생성됩니다"
          open={previewOpen}
          onOk={handleApprove}
          onCancel={() => {
            setPreviewOpen(false);
            setDryRun(null);
          }}
          okText={`승인 (${matched}개 생성 / ${replaceCnt}개 덮어쓰기)`}
          okButtonProps={{
            disabled: loading || !canApprove(dryRun?.stats),
            loading: approving,
          }}
          width={960}
          destroyOnClose
        >
          {loading ? (
            <Spin />
          ) : (
            dryRun && (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Descriptions column={5} size="small" bordered>
                  <Descriptions.Item label="Total">
                    {dryRun.stats.total ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Matched">
                    {dryRun.stats.matched}
                  </Descriptions.Item>
                  <Descriptions.Item label="Replace">{dryRun.stats.replace ?? 0}</Descriptions.Item>
                  <Descriptions.Item label="Duplicate">
                    {dryRun.stats.duplicate}
                  </Descriptions.Item>
                  <Descriptions.Item label="Invalid">
                    {dryRun.stats.invalid}
                  </Descriptions.Item>
                </Descriptions>
                <Table<BulkEpisodeRowResult>
                  rowKey="row_index"
                  size="small"
                  pagination={false}
                  dataSource={dryRun.rows}
                  columns={columns}
                  scroll={{ y: 360 }}
                />
              </Space>
            )
          )}
        </Modal>

        <Modal
          title={`작업 #${detailJob?.job_id ?? ''} 상세`}
          open={detailOpen}
          onCancel={() => {
            setDetailOpen(false);
            setDetailJob(null);
          }}
          footer={null}
          width={960}
          destroyOnClose
        >
          {detailLoading ? (
            <Spin />
          ) : (
            detailJob && (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Descriptions column={5} size="small" bordered>
                  <Descriptions.Item label="Status">
                    <Tag color={JOB_STATUS_COLOR[detailJob.status] ?? 'default'}>
                      {detailJob.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created">
                    {detailJob.stats?.created ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Replaced">
                    {detailJob.stats?.replaced ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Failed">
                    {detailJob.stats?.failed ?? '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total">
                    {detailJob.stats?.total ?? '-'}
                  </Descriptions.Item>
                </Descriptions>
                <Table<BulkEpisodeRowResult>
                  rowKey="row_index"
                  size="small"
                  pagination={false}
                  dataSource={detailJob.rows}
                  columns={columns}
                  scroll={{ y: 420 }}
                />
              </Space>
            )
          )}
        </Modal>
      </Card>
    </BasePage>
  );
};

export default BulkEpisodeIngest;
